import { Dialog } from "primereact/dialog";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { useTableNameFromUrl } from "../hooks/useTableNameFromUrl";
import { PossibleColumnProps, RowFromView, ViewNames } from "../types";
import { deliveryStatuses } from "../helpers/deliveryStatuses";
import * as React from "react";
import { api, HistoryEntry } from "../api";
import { OverlayPanel } from "primereact/overlaypanel";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Delivery } from "../types/delivery.ts";

type HistoryDialogProps<TView extends ViewNames> = {
  rowData: RowFromView<TView> | null;
  onHide: () => void;
  columns: PossibleColumnProps<RowFromView<TView>>[];
};

const columnNames: Record<HistoryEntry["field"], string> = {
  address: "Adres",
  broker: "Broker",
  broker_invoice: "Faktura brokera",
  buyer: "Nabywca",
  city: "Miasto",
  client: "Klient",
  client_rate: "Stawka klienta",
  company: "Firma",
  completion_deadline: "Termin realizacji",
  contact_person_name: "Osoba kontaktowa",
  contact_phone_number: "Numer kontaktowy",
  contract_number: "Numer umowy",
  contract_type: "Typ umowy",
  country: "Kraj",
  date: "Data",
  deleted_at: "Usunięto",
  driver: "Kierowca",
  email: "Email",
  entry_status: "Status wpisu",
  first_name: "Imię",
  id: "ID",
  is_active: "Aktywny",
  is_buyer: "Nabywca",
  is_supplier: "Dostawca",
  is_transporter: "Przewoźnik",
  last_name: "Nazwisko",
  latitude: "Szerokość geograficzna",
  loaded_weight: "Waga załadowana",
  loaded_weight_edited: "Waga załadowana",
  loading_date: "Data załadunku",
  loading_location: "Miejsce załadunku",
  location: "Lokalizacja",
  longitude: "Długość geograficzna",
  manager: "Menedżer",
  marked_fields: "Zaznaczone pola",
  name: "Nazwa",
  number: "Numer",
  password: "Hasło",
  payment_deadline_days: "Termin płatności (dni)",
  personal_code: "Kod osobisty",
  phone: "Telefon",
  price: "Cena",
  product: "Produkt",
  profit: "Zysk",
  purchase_contract: "Umowa zakupu",
  purchase_deductions: "Potrącenia z zakupu",
  purchase_invoice: "Faktura zakupu",
  purchase_order_number: "Numer zamówienia zakupu",
  purchase_price: "Cena zakupu",
  registry_code: "Kod rejestru",
  sales_contract: "Umowa sprzedaży",
  sales_deductions: "Potrącenia ze sprzedaży",
  sales_invoice: "Faktura sprzedaży",
  sales_order_number: "Numer zamówienia sprzedaży",
  sales_price: "Cena sprzedaży",
  status: "Status",
  supplier: "Dostawca",
  trailer: "Przyczepa",
  transport_rate: "Stawka transportu",
  transporter: "Przewoźnik",
  truck: "Ciężarówka",
  type: "Typ",
  unloaded_weight: "Waga rozładowana",
  unloaded_weight_edited: "Waga rozładowana",
  unloading_date: "Data rozładunku",
  unloading_location: "Miejsce rozładunku",
  waybiller_api_key: "Klucz API Waybiller",
  waybiller_api_url: "URL API Waybiller",
  waybiller_data_hash: "Hash danych Waybiller",
  waybiller_hidden_destination_raw_id:
    "Identyfikator ukrytego miejsca docelowego",
  waybiller_hide_destinations: "Ukryj miejsca docelowe przed dostawcami",
  waybiller_id: "ID Waybiller",
  waybiller_registry_code: "Kod rejestru Waybiller",
  weight: "Waga",
  weight_edited: "Waga",
  sort_rank: "Kolejność sortowania",
  sales_contract_template: "Szablon umowy sprzedaży",
  purchase_contract_template: "Szablon umowy zakupu",
  end_date: "Termin realizacji: do",
  start_date: "Termin realizacji: od",
  signed: "Podpisana?",
  legal_address: "Adres prawny",
  comment_created: "Komentarz utworzony",
  comment_edited: "Komentarz edytowany",
  comment_deleted: "Komentarz usunięty",
  file_extension: "Rozszerzenie pliku",
  file: "Plik",
};

const HISTORY_ROWS_PER_PAGE = 10;

const HistoryMarkedFieldsList = ({
  title,
  fields,
  markedColumnNames,
}: {
  title: React.ReactNode;
  fields: string[];
  markedColumnNames: Record<string, string>;
}) => {
  const sortedFields = React.useMemo(
    () =>
      fields.sort((a, b) =>
        (markedColumnNames[a] ?? a).localeCompare(markedColumnNames[b] ?? b),
      ),
    [fields, markedColumnNames],
  );
  const ref = React.useRef<OverlayPanel>(null);
  const onToggle = React.useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >((e) => {
    ref.current?.toggle(e);
  }, []);
  if (sortedFields.length === 0) {
    return null;
  }

  if (sortedFields.length === 1) {
    return (
      <Button type="button" disabled text>
        {title} {markedColumnNames[sortedFields[0]] ?? sortedFields[0]}
      </Button>
    );
  }

  return (
    <>
      <Button type="button" onClick={onToggle} text>
        {title} {sortedFields.length}
      </Button>
      <OverlayPanel ref={ref}>
        <div className="history-marked-fields-list">
          {sortedFields.map((field) => (
            <Chip key={field} label={markedColumnNames[field] ?? field} />
          ))}
        </div>
      </OverlayPanel>
    </>
  );
};

const MarkedFieldsBefore = ({
  before,
  markedColumnNames,
}: {
  before: string | null;
  markedColumnNames: Record<string, string>;
}) => {
  const fields = React.useMemo(
    () => (before ? before.split(",").map((f) => f.trim()) : []),
    [before],
  );
  return (
    <HistoryMarkedFieldsList
      fields={fields}
      title="Liczba oznaczonych pół:"
      markedColumnNames={markedColumnNames}
    />
  );
};

const MarkedFieldsAfter = ({
  before,
  after,
  markedColumnNames,
}: {
  before: string | null;
  after: string | null;
  markedColumnNames: Record<string, string>;
}) => {
  const beforeMarked = React.useMemo(
    () => new Set(before ? before.split(",").map((f) => f.trim()) : []),
    [before],
  );
  const afterMarked = React.useMemo(
    () => new Set(after ? after.split(",").map((f) => f.trim()) : []),
    [after],
  );
  const added = React.useMemo(
    () => Array.from(afterMarked.difference(beforeMarked)),
    [afterMarked, beforeMarked],
  );
  const removed = React.useMemo(
    () => Array.from(beforeMarked.difference(afterMarked)),
    [afterMarked, beforeMarked],
  );
  return (
    <>
      <HistoryMarkedFieldsList
        fields={added}
        title="Dodane pola:"
        markedColumnNames={markedColumnNames}
      />
      <HistoryMarkedFieldsList
        fields={removed}
        title="Usuniete pola:"
        markedColumnNames={markedColumnNames}
      />
    </>
  );
};

const isDeliveryRow = (row: unknown): row is Delivery =>
  typeof row === "object" &&
  row !== null &&
  "source" in row &&
  "objectId" in row &&
  (row.source === "TransportOrder" || row.source === "ForwardingOrder");

const HistoryValue = ({
  field,
  before,
  after,
  column,
  markedColumnNames,
}: {
  field: HistoryEntry["field"];
  before: string | null;
  after: string | null;
  column: "before" | "after";
  markedColumnNames: Record<string, string>;
}) => {
  const value = column === "before" ? before : after;
  if (value === null) {
    return "-";
  }
  if (field === "status") {
    return deliveryStatuses.find((s) => s.id === value)?.name ?? value;
  } else if (
    field === "loading_date" ||
    field === "unloading_date" ||
    field === "date" ||
    field === "start_date" ||
    field === "end_date"
  ) {
    return new Date(value).toLocaleString("pl-PL").split(",")[0];
  } else if (field === "marked_fields") {
    if (column === "before") {
      return (
        <MarkedFieldsBefore
          before={value}
          markedColumnNames={markedColumnNames}
        />
      );
    } else {
      return (
        <MarkedFieldsAfter
          before={before}
          after={value}
          markedColumnNames={markedColumnNames}
        />
      );
    }
  }
  return value;
};

export function HistoryDialog<TView extends ViewNames>({
  rowData,
  onHide,
  columns,
}: HistoryDialogProps<TView>) {
  const tableName = useTableNameFromUrl();
  const [historyPage, setHistoryPage] = React.useState(0);

  const historyColumnNames = React.useMemo(() => {
    return columns.reduce(
      (accumulator, currentValue) => ({
        ...accumulator,
        [currentValue.field]: currentValue.header,
      }),
      {} as Record<string, string>,
    );
  }, [columns]);

  // For deliveries, determine table and ID from the row data
  const actualTableName = React.useMemo(() => {
    if (tableName === null && isDeliveryRow(rowData)) {
      return rowData.source;
    }
    return tableName;
  }, [tableName, rowData]);

  const actualParentId = React.useMemo(() => {
    if (isDeliveryRow(rowData)) {
      // Delivery row - use objectId as parent ID
      return rowData.objectId;
    }
    return rowData?.id ?? 0;
  }, [rowData]);

  const { data: history, isFetching } =
    api.endpoints.listHistoryEntriesApiHistoryGet.useQuery(
      {
        table: actualTableName ?? undefined,
        parentId: actualParentId,
        orderBy: "-created_at,field",
        limit: HISTORY_ROWS_PER_PAGE,
        offset: historyPage * HISTORY_ROWS_PER_PAGE,
      },
      { skip: !actualTableName },
    );

  const preparedData: HistoryEntry[] = React.useMemo(() => {
    return history?.items ?? [];
  }, [history]);

  const onPage = React.useCallback((e: DataTablePageEvent) => {
    setHistoryPage(e.page ?? 10);
  }, []);

  const handleHide = React.useCallback(() => {
    onHide();
    setHistoryPage(0);
  }, [onHide]);

  return (
    <Dialog
      header="Dziennik zmian"
      visible={rowData !== null}
      onHide={handleHide}
      breakpoints={{ "2000px": "70vw", "960px": "75vw", "641px": "95vw" }}
    >
      <DataTable
        value={preparedData}
        lazy
        paginator
        loading={isFetching}
        emptyMessage="Nie znaleziono danych!"
        rows={HISTORY_ROWS_PER_PAGE}
        first={historyPage * HISTORY_ROWS_PER_PAGE}
        totalRecords={history?.total ?? 0}
        onPage={onPage}
        sortOrder={-1}
        sortField="created_at"
        size="small"
      >
        <Column
          field="created_at"
          header="Zmieniono o"
          body={(history: HistoryEntry) =>
            new Date(history.created_at).toLocaleString("pl-PL", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          }
        />
        <Column field="created_by_name" header="Zmieniony przez" />
        <Column
          field="field"
          header="Pole"
          body={(history: HistoryEntry) => (
            <>{columnNames[history.field] ?? history.field}</>
          )}
        />
        <Column
          field="before"
          header="Zanim"
          body={(history: HistoryEntry) => (
            <HistoryValue
              field={history.field}
              before={history.before}
              after={history.after}
              column="before"
              markedColumnNames={historyColumnNames}
            />
          )}
        />
        <Column
          field="after"
          header="Po"
          style={{ maxWidth: "20rem", overflow: "auto" }}
          body={(history: HistoryEntry) => (
            <HistoryValue
              field={history.field}
              before={history.before}
              after={history.after}
              column="after"
              markedColumnNames={historyColumnNames}
            />
          )}
        />
      </DataTable>
    </Dialog>
  );
}
